import React from "react";
import { formatearPorcentajeIrpf } from "@/utils/porcentaje";
import { centimosAEuros, importeFinitoOCero } from "@shared/importes";
import { AVISO_IMPORTES_INVALIDOS, calcularImportesDocumento } from "@/utils/importesDocumento";
import type { Cliente, FreelanceInfo, PaymentMethod, Tax, VerifactuData, InvoiceItem } from "@shared/types";

// Generic document type for preview (works with Factura, Presupuesto, Rectificativa)
interface PreviewDocument {
  tipo: string;
  numero: string;
  items: InvoiceItem[];
  aplicarIrpf?: boolean;
  verifactu?: VerifactuData;
  // Allow additional properties from different document types
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface DocumentPreviewProps {
  document: PreviewDocument;
  client: Cliente;
  freelance: FreelanceInfo;
  taxes: Tax[];
  paymentMethod?: PaymentMethod;
}

// Helper to check for property existence
function hasProperty<T extends object, K extends PropertyKey>(obj: T, key: K): obj is T & Record<K, any> {
  return key in obj;
}

export function DocumentPreview({ document, client, freelance, taxes, paymentMethod }: DocumentPreviewProps) {
  // Mismos importes que el formulario, Facturae y VeriFactu: céntimos enteros
  // con calcularImportes (IVA por grupo de tipo, IRPF sobre la base total).
  // En coma flotante, 3.950,50 × 0,15 = 592,5749… salía como 592,57 € en el
  // PDF, y 9,50 × 0,21 = 1,9949… como 1,99 €. importeFinitoOCero evita que un
  // importe no numérico lance durante el render y desmonte el formulario.
  //
  // En un documento emitido con instantánea (`importesEmision`) los importes y la
  // tasa de IRPF salen de ella, no del perfil ni de los impuestos de hoy.
  const aplicaIrpf = hasProperty(document, 'aplicarIrpf') && document.aplicarIrpf;
  const importes = calcularImportesDocumento(
    document,
    document.items.map((item: InvoiceItem) => ({
      cantidad: importeFinitoOCero(item.cantidad),
      precioUnitario: importeFinitoOCero(item.precioUnitario),
      rate: taxes.find((t: Tax) => t.id === item.taxId)?.rate ?? 0,
    })),
    aplicaIrpf ? (freelance.irpfRate || 0) : 0,
  );

  const { subtotal, iva, irpf, total } = importes;
  const tasaIrpfMostrada = importes.irpfRateEmision ?? freelance.irpfRate;
  // Con instantánea válida, el total descuenta el IRPF guardado: la fila sale de ella.
  const mostrarIrpf = importes.mostrarIrpf ?? Boolean(aplicaIrpf);

  const getTitle = () => {
    switch (document.tipo) {
      case 'FACTURA': return 'Factura';
      case 'PRESUPUESTO': return 'Presupuesto';
      case 'FACTURA_RECTIFICATIVA': return 'Factura Rectificativa';
      default: return 'Documento';
    }
  };

  return (
    <div id="document-preview-pdf" className="bg-white text-gray-900 mx-auto my-8 font-sans shadow-xl print:shadow-none" style={{ maxWidth: '210mm', minHeight: '297mm' }}>
      <div className="p-12 print:p-8">
        {importes.fuente === "instantanea-invalida" && (
          <div role="alert" className="mb-6 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 print:hidden">
            {AVISO_IMPORTES_INVALIDOS}
          </div>
        )}
        {/* Logo and Header */}
        <header className="flex justify-between items-start pb-6 border-b-2 border-gray-300 mb-6">
          <div className="flex-1 pr-8">
            {freelance.logoUrl && (
              <div className="mb-4">
                <img src={freelance.logoUrl} alt="Logo" className="max-h-20 object-contain" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{freelance.nombre}</h1>
            {freelance.nombreComercial && (
              <p className="text-base font-semibold text-gray-700 mb-2">{freelance.nombreComercial}</p>
            )}
            <div className="text-sm text-gray-600 space-y-0.5">
              <p>NIF: {freelance.nif}</p>
              <p>{freelance.direccion}</p>
              {freelance.codigoPostal && freelance.ciudad && (
                <p>{freelance.codigoPostal} {freelance.ciudad}</p>
              )}
              <p>{freelance.email}</p>
              {freelance.telefono && <p>Tel: {freelance.telefono}</p>}
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-3xl font-bold text-blue-600 mb-2">{getTitle()}</h2>
            <div className="text-sm text-gray-700 space-y-1">
              <p className="font-mono font-semibold text-base">#{document.numero}</p>
              <p><span className="font-semibold">Fecha:</span> {new Date(hasProperty(document, 'fechaEmision') ? document.fechaEmision : (document as any).fecha).toLocaleDateString('es-ES')}</p>
              {hasProperty(document, 'fechaVencimiento') && document.fechaVencimiento && (
                <p><span className="font-semibold">Vencimiento:</span> {new Date(document.fechaVencimiento).toLocaleDateString('es-ES')}</p>
              )}
              {hasProperty(document, 'referenciaFactura') && document.referenciaFactura && (
                <p><span className="font-semibold">Ref. Factura:</span> {document.referenciaFactura}</p>
              )}
            </div>
          </div>
        </header>

        {/* Rectificativa Info - Only for rectificative invoices */}
        {document.tipo === 'FACTURA_RECTIFICATIVA' && hasProperty(document, 'facturaOriginalNumero') && (
          <section className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
            <h3 className="text-sm font-bold text-red-800 mb-2 uppercase tracking-wide">Información de Rectificación</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p><span className="font-semibold">Factura Original:</span> {document.facturaOriginalNumero}</p>
              {hasProperty(document, 'motivo') && document.motivo && (
                <p><span className="font-semibold">Motivo:</span> {document.motivo}</p>
              )}
            </div>
          </section>
        )}

        {/* Client Info */}
        <section className="mb-8 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-base font-bold text-gray-800 mb-3 uppercase tracking-wide">Datos del Cliente</h3>
          <div className="text-sm text-gray-700 space-y-0.5">
            <p className="font-semibold text-gray-900">{client.nombre}</p>
            <p>NIF: {client.nif}</p>
            <p>{client.direccion}</p>
            {client.codigoPostal && client.ciudad && (
              <p>{client.codigoPostal} {client.ciudad}</p>
            )}
            {client.email && <p>Email: {client.email}</p>}
          </div>
        </section>

        {/* Items Table */}
        <section className="mb-8">
          <h3 className="text-base font-bold text-gray-800 mb-3 uppercase tracking-wide">Detalle de Servicios/Productos</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-800 text-white text-xs font-semibold">
                  <th className="py-3 px-3 text-left">Concepto</th>
                  <th className="py-3 px-2 text-center w-16">Cant.</th>
                  <th className="py-3 px-2 text-right w-24">P. Unit.</th>
                  <th className="py-3 px-2 text-center w-20">IVA</th>
                  <th className="py-3 px-2 text-right w-24">Base</th>
                  <th className="py-3 px-2 text-right w-24">Cuota IVA</th>
                  <th className="py-3 px-3 text-right w-28">Total</th>
                </tr>
              </thead>
              <tbody className="text-gray-800 text-xs">
                {document.items.map((item: InvoiceItem, index: number) => {
                  const linea = importes.resultado.lineas[index];
                  // El tipo de la fila es el que se usó para calcular sus importes: el guardado
                  // al emitir si hay instantánea válida, el de hoy en otro caso.
                  const taxRate = linea.rate;
                  const baseImponible = centimosAEuros(linea.baseCentimos);
                  const cuotaIva = centimosAEuros(linea.cuotaCentimos);
                  const totalConIva = centimosAEuros(linea.baseCentimos + linea.cuotaCentimos);

                  return (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-3">
                        <div className="font-semibold text-sm">{item.concepto}</div>
                        {item.descripcion && (
                          <div className="text-xs text-gray-600 mt-1">{item.descripcion}</div>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">{item.cantidad}</td>
                      <td className="py-3 px-2 text-right font-mono">{item.precioUnitario.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                      <td className="py-3 px-2 text-center">
                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                          {(taxRate * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right font-mono">{baseImponible.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                      <td className="py-3 px-2 text-right font-mono text-blue-700">{cuotaIva.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-sm">{totalConIva.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Observaciones */}
        {freelance.observaciones && (
          <section className="mb-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
            <h3 className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">Observaciones</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{freelance.observaciones}</p>
          </section>
        )}

        {/* Totals */}
        <section className="mb-8 flex justify-end">
          <div className="w-full md:w-1/2 lg:w-2/5">
            <table className="w-full text-sm border border-gray-300">
              <tbody>
                <tr className="bg-gray-50">
                  <td className="py-2.5 px-4 font-semibold text-gray-700">Subtotal</td>
                  <td className="py-2.5 px-4 text-right font-mono">{subtotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                </tr>
                <tr className="border-t border-gray-300">
                  <td className="py-2.5 px-4 font-semibold text-gray-700">IVA</td>
                  <td className="py-2.5 px-4 text-right font-mono">{iva.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                </tr>
                {mostrarIrpf && (
                  <tr className="border-t border-gray-300">
                    <td className="py-2.5 px-4 font-semibold text-gray-700">IRPF ({formatearPorcentajeIrpf(tasaIrpfMostrada)}%)</td>
                    <td className="py-2.5 px-4 text-right font-mono text-red-600">-{irpf.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                  </tr>
                )}
                <tr className="border-t-2 border-gray-800 bg-gray-800 text-white">
                  <td className="py-3 px-4 text-base font-bold">TOTAL</td>
                  <td className="py-3 px-4 text-right font-mono text-lg font-bold">{total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Payment Method */}
        {paymentMethod && (
          <section className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">Método de Pago</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p className="font-semibold">{paymentMethod.nombre}</p>
              {paymentMethod.iban && (
                <p><span className="font-semibold">IBAN:</span> <span className="font-mono">{paymentMethod.iban}</span></p>
              )}
              {!paymentMethod.iban && freelance.iban && (
                <p><span className="font-semibold">IBAN:</span> <span className="font-mono">{freelance.iban}</span></p>
              )}
              {paymentMethod.banco && (
                <p><span className="font-semibold">Banco:</span> {paymentMethod.banco}</p>
              )}
            </div>
          </section>
        )}

        {/* Document Notes */}
        {hasProperty(document, 'notas') && document.notas && (
          <section className="mb-8 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
            <h3 className="text-sm font-bold text-gray-800 mb-2 uppercase tracking-wide">Notas del Documento</h3>
            <p className="text-sm text-gray-700 italic whitespace-pre-wrap">{document.notas}</p>
          </section>
        )}

        {/* Custom Footer */}
        {freelance.footer && (
          <section className="mb-6">
            <div className="text-xs text-gray-600 text-center whitespace-pre-wrap border-t border-gray-300 pt-4">
              {freelance.footer}
            </div>
          </section>
        )}

        {/* Verifactu Section - Only for invoices and rectificative invoices with Verifactu data */}
        {(document.tipo === 'FACTURA' || document.tipo === 'FACTURA_RECTIFICATIVA') && hasProperty(document, 'verifactu') && document.verifactu && (
          <section className="mb-6 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
            <div className="flex items-center mb-3">
              <div className="flex-1">
                <h3 className="text-sm font-bold text-green-800 mb-1 uppercase tracking-wide flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  VERIFACTU - Sistema de Integridad
                </h3>
                <p className="text-xs text-green-700">
                  Esta factura está registrada en el sistema Verifactu según la normativa de la Agencia Tributaria Española
                </p>
              </div>
              {document.verifactu.qrCode && (
                <div className="ml-4 bg-white p-2 rounded border-2 border-green-400">
                  <img
                    src={document.verifactu.qrCode}
                    alt="Código QR de verificación"
                    className="w-24 h-24"
                  />
                  <p className="text-xs text-center text-gray-600 mt-1">Escanea para verificar</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-semibold text-gray-700">Número de Registro:</span>
                <span className="ml-2 font-mono text-green-800">#{document.verifactu.numeroRegistro}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">Fecha de Registro:</span>
                <span className="ml-2">{new Date(document.verifactu.fechaHoraRegistro).toLocaleString('es-ES')}</span>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-green-200">
              <p className="text-xs text-gray-700 mb-2">
                <span className="font-semibold">Hash de Integridad (SHA-256):</span>
              </p>
              <p className="font-mono text-xs text-gray-600 break-all bg-white p-2 rounded border border-green-200">
                {document.verifactu.hash}
              </p>
            </div>

            {document.verifactu.urlVerificacion && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <p className="text-xs text-gray-700 mb-1">
                  <span className="font-semibold">Verificación Pública:</span>
                </p>
                <p className="text-xs text-blue-600 break-all underline">
                  {document.verifactu.urlVerificacion}
                </p>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-green-200 text-xs text-gray-600">
              <p>
                <strong>Cumplimiento normativo:</strong> Esta factura cumple con la Orden HAC/1177/2024 sobre sistemas informáticos
                de facturación. Los datos están protegidos mediante hash criptográfico y forman parte de una cadena de integridad inmutable.
              </p>
            </div>
          </section>
        )}

        {/* Default Footer */}
        <footer className="text-center text-xs text-gray-500 pt-4 border-t border-gray-200">
          <p>Gracias por su confianza.</p>
        </footer>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:p-8 {
            padding: 2rem !important;
          }
          .hover\\:bg-gray-50:hover {
            background-color: transparent !important;
          }
        }
      `}</style>
    </div>
  );
}
